import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Loader2, ArrowLeft, ArrowRight, Check, Image as ImageIcon, Sparkles, AlertCircle, Upload, XCircle, Clock } from 'lucide-react'
import { http } from '@/lib/api-client'
import {
  useCarBrands,
  useCarModels,
  useCarGenerations,
  useCarVariants,
  useCarFeatures,
  useCreateCarListing,
  useUpdateCarListing,
  useRequestModel,
  usePlatformUsage,
} from '../../hooks/use-cars'
import { useNavigate } from 'react-router-dom'

interface ListingWizardProps {
  initialData?: any
  isEdit?: boolean
  redirectPath: string
}

export function ListingWizardForm({ initialData, isEdit = false, redirectPath }: ListingWizardProps) {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)

  // Step 1: Canonical
  const [brandId, setBrandId] = useState<number | undefined>(initialData?.brandId)
  const [modelId, setModelId] = useState<number | undefined>(initialData?.modelId)
  const [generationId, setGenerationId] = useState<number | undefined>(initialData?.generationId)
  const [variantId, setVariantId] = useState<number | undefined>(initialData?.variantId)

  // Cascading queries
  const { data: brands } = useCarBrands()
  const { data: models } = useCarModels(brandId)
  const { data: generations } = useCarGenerations(modelId)
  const { data: variants } = useCarVariants(generationId)
  const { data: featureCatalog } = useCarFeatures()

  // Step 2: Specs & Details
  const [title, setTitle] = useState(initialData?.title || '')
  const [hasUserEditedTitle, setHasUserEditedTitle] = useState(Boolean(initialData?.title))
  const [manufactureYear, setManufactureYear] = useState<string>(initialData?.manufactureYear ? String(initialData.manufactureYear) : '2022')
  const [mileageKm, setMileageKm] = useState<string>(initialData?.mileageKm ? String(initialData.mileageKm) : '25000')
  const [fuelType, setFuelType] = useState(initialData?.fuelType || 'PETROL')
  const [transmission, setTransmission] = useState(initialData?.transmission || 'AUTOMATIC')
  const [condition, setCondition] = useState(initialData?.condition || 'USED')
  const [ownership, setOwnership] = useState(initialData?.ownership || '1st Owner')
  const [driveType, setDriveType] = useState(initialData?.driveType || 'FWD')
  const [exteriorColor, setExteriorColor] = useState(initialData?.exteriorColor || 'Pearl White')

  // Step 3: Features
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(initialData?.features || [])

  // Step 4: Media
  const [imageUrls, setImageUrls] = useState<string[]>(
    Array.isArray(initialData?.images)
      ? initialData.images.map((img: any) => (typeof img === 'string' ? img : img.url))
      : ['https://images.unsplash.com/photo-1542282088-72c9c27ed0cd?auto=format&fit=crop&w=1200&q=80']
  )
  const [newImageInput, setNewImageInput] = useState('')
  const [uploadingImages, setUploadingImages] = useState(false)
  const [uploadError, setUploadError] = useState('')

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    setUploadingImages(true)
    setUploadError('')
    try {
      const formData = new FormData()
      Array.from(files).forEach((file) => formData.append('images', file))
      const res = await http.post<{ success: boolean; data: { urls: string[] } }>('/cars/upload/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (res.data?.data?.urls) {
        setImageUrls((prev) => [...prev, ...res.data.data.urls])
      }
    } catch (err: any) {
      setUploadError(err.response?.data?.message || err.message || 'Image upload failed')
    } finally {
      setUploadingImages(false)
      e.target.value = ''
    }
  }

  // Step 5: Pricing & Location
  const [askingPrice, setAskingPrice] = useState<string>(initialData?.askingPrice ? String(initialData.askingPrice) : '1500000')
  const [negotiable, setNegotiable] = useState(initialData?.negotiable ?? true)
  const [locationCity, setLocationCity] = useState(initialData?.locationCity || 'Mumbai')
  const [locationState, setLocationState] = useState(initialData?.locationState || 'Maharashtra')
  const [description, setDescription] = useState(initialData?.description || '')

  // Model Request Dialog
  const [requestDialogOpen, setRequestDialogOpen] = useState(false)
  const [reqBrand, setReqBrand] = useState('')
  const [reqModel, setReqModel] = useState('')
  const [reqNotes, setReqNotes] = useState('')

  const createListing = useCreateCarListing()
  const updateListing = useUpdateCarListing()
  const requestModel = useRequestModel()
  const { data: usage } = usePlatformUsage()

  const maxImagesPerListing = usage?.maxImagesPerListing ?? 10

  // Auto-generate title only when user hasn't explicitly customized it
  useEffect(() => {
    if (!hasUserEditedTitle && !initialData?.title && brandId && modelId && brands && models) {
      const b = brands.find((x: any) => x.id === brandId)?.name || ''
      const m = models.find((x: any) => x.id === modelId)?.name || ''
      if (b && m) {
        setTitle(`${manufactureYear} ${b} ${m}`.trim())
      }
    }
  }, [brandId, modelId, manufactureYear, brands, models, hasUserEditedTitle, initialData?.title])

  const toggleFeature = (featName: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(featName) ? prev.filter((f) => f !== featName) : [...prev, featName]
    )
  }

  const addImage = () => {
    if (!newImageInput.trim()) return
    setImageUrls((prev) => [...prev, newImageInput.trim()])
    setNewImageInput('')
  }

  const removeImage = (idx: number) => {
    setImageUrls((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleModelRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    await requestModel.mutateAsync({
      brandName: reqBrand,
      modelName: reqModel,
      notes: reqNotes,
    })
    setReqBrand('')
    setReqModel('')
    setReqNotes('')
    setRequestDialogOpen(false)
  }

  const handleSubmit = async (asDraft: boolean) => {
    if (!brandId || !modelId) return

    const payload = {
      brandId,
      modelId,
      generationId,
      variantId,
      title: title || 'Vehicle Listing',
      askingPrice: Number(askingPrice),
      negotiable,
      manufactureYear: Number(manufactureYear),
      mileageKm: Number(mileageKm),
      fuelType,
      transmission,
      condition,
      ownership,
      driveType,
      exteriorColor,
      locationCity,
      locationState,
      description,
      features: selectedFeatures,
      images: imageUrls.map((url, index) => ({ url, isPrimary: index === 0, order: index })),
      status: asDraft ? 'DRAFT' : 'PENDING_REVIEW',
      action: asDraft ? 'SAVE_DRAFT' : 'SUBMIT',
    }

    if (isEdit && initialData?.id) {
      await updateListing.mutateAsync({ id: initialData.id, payload })
    } else {
      await createListing.mutateAsync(payload)
    }

    navigate(redirectPath)
  }

  const stepsList = [
    { num: 1, label: 'Vehicle' },
    { num: 2, label: 'Specifications' },
    { num: 3, label: 'Features' },
    { num: 4, label: 'Media' },
    { num: 5, label: 'Pricing & Location' },
    { num: 6, label: 'Preview & Submit' },
  ]

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Moderation Status Banner in Edit Mode */}
      {isEdit && initialData?.status === 'REJECTED' && (
        <Card className="border-rose-500/40 bg-rose-500/10 p-4">
          <div className="flex items-start gap-3">
            <XCircle className="size-5 text-rose-500 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h4 className="font-bold text-sm text-rose-500 uppercase tracking-wider">
                Listing Rejected by Platform Moderator
              </h4>
              <p className="text-xs font-semibold text-foreground">
                Reason: {initialData.rejectionReason || 'Listing did not meet platform quality standards.'}
              </p>
              <p className="text-xs text-muted-foreground">
                Please correct the details below and click <strong>Submit Listing</strong> to send it back for Admin review.
              </p>
            </div>
          </div>
        </Card>
      )}

      {isEdit && initialData?.status === 'PENDING_REVIEW' && (
        <Card className="border-amber-500/40 bg-amber-500/10 p-4">
          <div className="flex items-start gap-3">
            <Clock className="size-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h4 className="font-bold text-sm text-amber-500 uppercase tracking-wider">
                Pending Admin Review
              </h4>
              <p className="text-xs text-muted-foreground">
                This vehicle listing is currently waiting for platform moderator review. Any edits saved will remain pending until approved.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Step Progress Header */}
      <div className="flex items-center justify-between border border-slate-200 dark:border-border/80 bg-white dark:bg-card/80 p-4 rounded-2xl shadow-xs overflow-x-auto gap-2">
        {stepsList.map((s) => (
          <div
            key={s.num}
            onClick={() => s.num < step && setStep(s.num)}
            className={`flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-xl transition-all cursor-pointer ${
              step === s.num
                ? 'bg-primary/10 text-primary border border-primary/30 shadow-xs'
                : s.num < step
                ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30'
                : 'text-slate-400 dark:text-muted-foreground opacity-50 pointer-events-none'
            }`}
          >
            <span className={`size-6 rounded-full flex items-center justify-center text-xs font-extrabold ${
              step === s.num ? 'bg-primary text-primary-foreground shadow-xs' : s.num < step ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-accent text-slate-600 dark:text-muted-foreground'
            }`}>
              {s.num < step ? <Check className="size-3.5" /> : s.num}
            </span>
            <span className="whitespace-nowrap">{s.label}</span>
          </div>
        ))}
      </div>

      {/* STEP 1: CANONICAL HIERARCHY */}
      {step === 1 && (
        <Card className="p-6 sm:p-8 space-y-6 border border-slate-200 dark:border-border/80 bg-white dark:bg-card/90 shadow-md rounded-2xl">
          <div className="border-b border-slate-100 dark:border-border/60 pb-4">
            <h3 className="font-display text-xl font-bold text-foreground">Step 1: Select Canonical Vehicle</h3>
            <p className="text-xs text-slate-500 dark:text-muted-foreground">Cascading master data selection ensures accurate marketplace taxonomy.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">1. Manufacturer Brand *</label>
              <select
                value={brandId || ''}
                onChange={(e) => {
                  setBrandId(Number(e.target.value))
                  setModelId(undefined)
                  setGenerationId(undefined)
                  setVariantId(undefined)
                }}
                className="w-full h-11 rounded-xl border border-slate-300 dark:border-border bg-slate-50/70 dark:bg-background/80 px-3.5 py-2 text-sm font-medium text-foreground shadow-xs transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white dark:focus:bg-background outline-none disabled:opacity-50"
              >
                <option value="">Select Brand</option>
                {(brands || []).map((b: any) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">2. Vehicle Model *</label>
              <select
                disabled={!brandId}
                value={modelId || ''}
                onChange={(e) => {
                  setModelId(Number(e.target.value))
                  setGenerationId(undefined)
                  setVariantId(undefined)
                }}
                className="w-full h-11 rounded-xl border border-slate-300 dark:border-border bg-slate-50/70 dark:bg-background/80 px-3.5 py-2 text-sm font-medium text-foreground shadow-xs transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white dark:focus:bg-background outline-none disabled:opacity-50"
              >
                <option value="">Select Model</option>
                {(models || []).map((m: any) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">3. Generation (Year Cycle)</label>
              <select
                disabled={!modelId}
                value={generationId || ''}
                onChange={(e) => {
                  setGenerationId(Number(e.target.value))
                  setVariantId(undefined)
                }}
                className="w-full h-11 rounded-xl border border-slate-300 dark:border-border bg-slate-50/70 dark:bg-background/80 px-3.5 py-2 text-sm font-medium text-foreground shadow-xs transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white dark:focus:bg-background outline-none disabled:opacity-50"
              >
                <option value="">Select Generation</option>
                {(generations || []).map((g: any) => (
                  <option key={g.id} value={g.id}>{g.name} ({g.startYear}-{g.endYear || 'Present'})</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">4. Trim Variant &amp; Specs</label>
              <select
                disabled={!generationId}
                value={variantId || ''}
                onChange={(e) => setVariantId(Number(e.target.value))}
                className="w-full h-11 rounded-xl border border-slate-300 dark:border-border bg-slate-50/70 dark:bg-background/80 px-3.5 py-2 text-sm font-medium text-foreground shadow-xs transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white dark:focus:bg-background outline-none disabled:opacity-50"
              >
                <option value="">Select Trim Variant</option>
                {(variants || []).map((v: any) => (
                  <option key={v.id} value={v.id}>{v.name} ({v.fuelType} • {v.transmission})</option>
                ))}
              </select>
            </div>
          </div>

          {/* Model Request Callout */}
          <div className="p-4 rounded-xl border border-primary/30 bg-primary/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="size-5 text-primary shrink-0" />
              <div className="text-xs">
                <p className="font-bold text-foreground">Can't find your vehicle brand or model?</p>
                <p className="text-slate-500 dark:text-muted-foreground">Submit a model request to Platform Administrators for canonical inclusion.</p>
              </div>
            </div>
            <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
              <DialogTrigger>
                <Button size="sm" variant="outline" className="text-xs font-bold border-primary/40 text-primary hover:bg-primary/10">Request Model</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request Unlisted Vehicle Model</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleModelRequest} className="space-y-4 pt-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold uppercase text-slate-700 dark:text-muted-foreground">Brand Name *</label>
                    <Input placeholder="e.g. Porsche" value={reqBrand} onChange={(e) => setReqBrand(e.target.value)} required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold uppercase text-slate-700 dark:text-muted-foreground">Model Name *</label>
                    <Input placeholder="e.g. 911 GT3" value={reqModel} onChange={(e) => setReqModel(e.target.value)} required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold uppercase text-slate-700 dark:text-muted-foreground">Notes / Year</label>
                    <Input placeholder="e.g. 2023 GT3 RS Trim" value={reqNotes} onChange={(e) => setReqNotes(e.target.value)} />
                  </div>
                  <div className="pt-4 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
                    <Button type="submit">Submit Request</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </Card>
      )}

      {/* STEP 2: SPECIFICATIONS */}
      {step === 2 && (
        <Card className="p-6 sm:p-8 space-y-6 border border-slate-200 dark:border-border/80 bg-white dark:bg-card/90 shadow-md rounded-2xl">
          <div className="border-b border-slate-100 dark:border-border/60 pb-4">
            <h3 className="font-display text-xl font-bold text-foreground">Step 2: Listing Specifications</h3>
            <p className="text-xs text-slate-500 dark:text-muted-foreground">Collect listing-specific details for prospective buyers.</p>
          </div>

          <div className="space-y-5">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">
                  Listing Title *
                </label>
                {brandId && modelId && (
                  <button
                    type="button"
                    onClick={() => {
                      setHasUserEditedTitle(false)
                      const b = brands?.find((x: any) => x.id === brandId)?.name || ''
                      const m = models?.find((x: any) => x.id === modelId)?.name || ''
                      setTitle(`${manufactureYear} ${b} ${m}`.trim())
                    }}
                    className="text-[11px] font-semibold text-primary hover:underline flex items-center gap-1"
                  >
                    <Sparkles className="size-3" /> Auto-suggest title
                  </button>
                )}
              </div>
              <Input
                placeholder="e.g. 2022 Toyota Fortuner Legender 2.8 4x4 AT"
                value={title}
                onChange={(e) => {
                  setHasUserEditedTitle(true)
                  setTitle(e.target.value)
                }}
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">Manufacture Year *</label>
                <Input type="number" value={manufactureYear} onChange={(e) => setManufactureYear(e.target.value)} required />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">Kilometers Driven *</label>
                <Input type="number" value={mileageKm} onChange={(e) => setMileageKm(e.target.value)} required />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">Ownership *</label>
                <select
                  value={ownership}
                  onChange={(e) => setOwnership(e.target.value)}
                  className="w-full h-11 rounded-xl border border-slate-300 dark:border-border bg-slate-50/70 dark:bg-background/80 px-3.5 py-2 text-sm font-medium text-foreground shadow-xs transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white dark:focus:bg-background outline-none"
                >
                  <option value="1st Owner">1st Owner</option>
                  <option value="2nd Owner">2nd Owner</option>
                  <option value="3rd Owner">3rd Owner</option>
                  <option value="4th+ Owner">4th+ Owner</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">Fuel Type</label>
                <select
                  value={fuelType}
                  onChange={(e) => setFuelType(e.target.value)}
                  className="w-full h-11 rounded-xl border border-slate-300 dark:border-border bg-slate-50/70 dark:bg-background/80 px-3.5 py-2 text-sm font-medium text-foreground shadow-xs transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white dark:focus:bg-background outline-none"
                >
                  <option value="PETROL">Petrol</option>
                  <option value="DIESEL">Diesel</option>
                  <option value="ELECTRIC">Electric</option>
                  <option value="HYBRID">Hybrid</option>
                  <option value="CNG">CNG</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">Transmission</label>
                <select
                  value={transmission}
                  onChange={(e) => setTransmission(e.target.value)}
                  className="w-full h-11 rounded-xl border border-slate-300 dark:border-border bg-slate-50/70 dark:bg-background/80 px-3.5 py-2 text-sm font-medium text-foreground shadow-xs transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white dark:focus:bg-background outline-none"
                >
                  <option value="AUTOMATIC">Automatic</option>
                  <option value="MANUAL">Manual</option>
                  <option value="CVT">CVT</option>
                  <option value="DCT">DCT</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">Condition</label>
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  className="w-full h-11 rounded-xl border border-slate-300 dark:border-border bg-slate-50/70 dark:bg-background/80 px-3.5 py-2 text-sm font-medium text-foreground shadow-xs transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white dark:focus:bg-background outline-none"
                >
                  <option value="USED">Pre-Owned (Used)</option>
                  <option value="NEW">Brand New</option>
                  <option value="CERTIFIED">Certified Pre-Owned</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">Exterior Color</label>
                <Input value={exteriorColor} onChange={(e) => setExteriorColor(e.target.value)} />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* STEP 3: FEATURES */}
      {step === 3 && (
        <Card className="p-6 sm:p-8 space-y-6 border border-slate-200 dark:border-border/80 bg-white dark:bg-card/90 shadow-md rounded-2xl">
          <div className="border-b border-slate-100 dark:border-border/60 pb-4">
            <h3 className="font-display text-xl font-bold text-foreground">Step 3: Vehicle Features &amp; Options</h3>
            <p className="text-xs text-slate-500 dark:text-muted-foreground">Select installed options from platform taxonomy.</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {(featureCatalog || []).map((feat: any) => {
              const isSelected = selectedFeatures.includes(feat.name)
              return (
                <div
                  key={feat.id}
                  onClick={() => toggleFeature(feat.name)}
                  className={`p-3.5 rounded-xl border cursor-pointer transition-all flex items-center justify-between text-xs font-bold shadow-2xs ${
                    isSelected
                      ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary/30'
                      : 'border-slate-300 dark:border-border bg-slate-50/50 dark:bg-background/60 hover:bg-slate-100 dark:hover:bg-accent/40 text-slate-700 dark:text-muted-foreground'
                  }`}
                >
                  <span>{feat.name}</span>
                  {isSelected && <Check className="size-4 text-primary shrink-0" />}
                </div>
              )
            })}
          </div>
        </Card>
      )}

      {/* STEP 4: MEDIA */}
      {step === 4 && (
        <Card className="p-6 sm:p-8 space-y-6 border border-slate-200 dark:border-border/80 bg-white dark:bg-card/90 shadow-md rounded-2xl">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-border/60 pb-4">
            <div>
              <h3 className="font-display text-xl font-bold text-foreground">Step 4: Image &amp; Media Gallery</h3>
              <p className="text-xs text-slate-500 dark:text-muted-foreground">Upload photos directly or paste image URLs (Primary cover image marked first).</p>
            </div>
            <Badge variant={imageUrls.length >= maxImagesPerListing ? 'destructive' : 'outline'} className="font-bold text-xs">
              {imageUrls.length} / {maxImagesPerListing} Photos Added
            </Badge>
          </div>

          {imageUrls.length >= maxImagesPerListing && (
            <div className="p-3.5 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-semibold flex items-center gap-2">
              <AlertCircle className="size-4 shrink-0" />
              <span>Photo Limit Reached: Your subscription plan permits up to {maxImagesPerListing} photos per listing. Delete an existing image to upload new ones.</span>
            </div>
          )}

          {/* Multer Direct File Upload */}
          <div className={`border-2 border-dashed border-slate-300 dark:border-border rounded-2xl p-6 text-center space-y-3 bg-slate-50/50 dark:bg-accent/10 transition-all ${
            imageUrls.length >= maxImagesPerListing ? 'opacity-50 pointer-events-none' : 'hover:bg-slate-100 dark:hover:bg-accent/20'
          }`}>
            <Upload className="size-8 text-primary mx-auto" />
            <div>
              <p className="text-sm font-bold text-foreground">Upload Vehicle Photos</p>
              <p className="text-xs text-slate-500 dark:text-muted-foreground">Select JPG, PNG, WEBP files up to 10MB per file.</p>
            </div>
            <label className="inline-flex cursor-pointer">
              <span className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-bold text-xs rounded-xl shadow-md hover:bg-primary/90 transition-all">
                {uploadingImages ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                {uploadingImages ? 'Uploading Photos...' : 'Choose Photos from Computer'}
              </span>
              <input
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/avif"
                onChange={handleFileUpload}
                disabled={uploadingImages || imageUrls.length >= maxImagesPerListing}
                className="hidden"
              />
            </label>
            {uploadError && <p className="text-xs text-destructive font-medium">{uploadError}</p>}
          </div>

          <div className="flex gap-2 items-center pt-2">
            <Input
              placeholder="Or paste external Image URL (https://...)"
              value={newImageInput}
              onChange={(e) => setNewImageInput(e.target.value)}
              disabled={imageUrls.length >= maxImagesPerListing}
            />
            <Button onClick={addImage} type="button" variant="secondary" disabled={imageUrls.length >= maxImagesPerListing} className="gap-2 shrink-0 font-bold border border-slate-300 dark:border-border">
              <ImageIcon className="size-4" /> Add URL
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {imageUrls.map((url, i) => (
              <div key={i} className="relative group rounded-xl overflow-hidden border border-slate-200 dark:border-border aspect-video bg-slate-100 dark:bg-accent/20 shadow-2xs">
                <img src={url} alt="" className="size-full object-cover" />
                {i === 0 && (
                  <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] font-bold">Primary Cover</Badge>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  className="absolute bottom-2 right-2 size-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeImage(i)}
                >
                  ×
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* STEP 5: PRICING & LOCATION */}
      {step === 5 && (
        <Card className="p-6 sm:p-8 space-y-6 border border-slate-200 dark:border-border/80 bg-white dark:bg-card/90 shadow-md rounded-2xl">
          <div className="border-b border-slate-100 dark:border-border/60 pb-4">
            <h3 className="font-display text-xl font-bold text-foreground">Step 5: Pricing &amp; Location</h3>
            <p className="text-xs text-slate-500 dark:text-muted-foreground">Set asking price and dealership location.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">Asking Price (₹) *</label>
              <Input type="number" value={askingPrice} onChange={(e) => setAskingPrice(e.target.value)} required />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">Price Negotiation</label>
              <select
                value={negotiable ? 'YES' : 'NO'}
                onChange={(e) => setNegotiable(e.target.value === 'YES')}
                className="w-full h-11 rounded-xl border border-slate-300 dark:border-border bg-slate-50/70 dark:bg-background/80 px-3.5 py-2 text-sm font-medium text-foreground shadow-xs transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white dark:focus:bg-background outline-none"
              >
                <option value="YES">Negotiable Price</option>
                <option value="NO">Fixed Price</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">City *</label>
              <Input value={locationCity} onChange={(e) => setLocationCity(e.target.value)} required />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">State *</label>
              <Input value={locationState} onChange={(e) => setLocationState(e.target.value)} required />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-muted-foreground block">Vehicle Description &amp; Condition Remarks</label>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-xl border border-slate-300 dark:border-border bg-slate-50/70 dark:bg-background/80 p-3.5 text-sm text-foreground shadow-xs transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:bg-white dark:focus:bg-background outline-none"
              placeholder="Describe vehicle condition, service history, and additional packages..."
            />
          </div>
        </Card>
      )}

      {/* STEP 6: PREVIEW & SUBMIT */}
      {step === 6 && (
        <Card className="p-6 sm:p-8 space-y-6 border border-slate-200 dark:border-border/80 bg-white dark:bg-card/90 shadow-md rounded-2xl">
          <div className="border-b border-slate-100 dark:border-border/60 pb-4">
            <h3 className="font-display text-xl font-bold text-foreground">Step 6: Preview &amp; Submit</h3>
            <p className="text-xs text-slate-500 dark:text-muted-foreground">Verify all vehicle details before saving as draft or submitting for Admin review.</p>
          </div>

          <div className="border border-slate-200 dark:border-border rounded-xl p-5 space-y-4 bg-slate-50/50 dark:bg-background/50">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-xl text-foreground">{title}</h4>
              <span className="font-display text-2xl font-extrabold text-primary">₹{Number(askingPrice).toLocaleString('en-IN')}</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-semibold text-slate-600 dark:text-muted-foreground">
              <div>Year: <span className="text-foreground font-bold">{manufactureYear}</span></div>
              <div>Mileage: <span className="text-foreground font-bold">{Number(mileageKm).toLocaleString('en-IN')} km</span></div>
              <div>Fuel: <span className="text-foreground font-bold">{fuelType}</span></div>
              <div>Location: <span className="text-foreground font-bold">{locationCity}, {locationState}</span></div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-slate-200 dark:border-border">
            <Button variant="outline" onClick={() => handleSubmit(true)} disabled={createListing.isPending || updateListing.isPending} className="font-bold border-slate-300 dark:border-border">
              Save Draft
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2 shadow-md" onClick={() => handleSubmit(false)} disabled={createListing.isPending || updateListing.isPending}>
              {createListing.isPending || updateListing.isPending ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />} Submit Listing
            </Button>
          </div>
        </Card>
      )}

      {/* Wizard Footer Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-border">
        <Button variant="outline" disabled={step === 1} onClick={() => setStep((s) => s - 1)} className="gap-2 font-bold border-slate-300 dark:border-border">
          <ArrowLeft className="size-4" /> Previous
        </Button>
        {step < 6 && (
          <Button onClick={() => setStep((s) => s + 1)} disabled={step === 1 && (!brandId || !modelId)} className="gap-2 font-bold shadow-md">
            Next <ArrowRight className="size-4" />
          </Button>
        )}
      </div>
    </div>
  )
}
